/**
 * Role-aware navigation profiles.
 * Admin ERP nav lives in DashboardLayout; this file supplies workspace menus
 * for field, operations, warehouse, sales hierarchy, and HR.
 */

import { canAccessActivityLogs, hasRoleWorkspace } from './roles';

/** Paths hidden from Salesman — they work through draft workflows only. */
export const SALESMAN_HIDDEN_PATHS = new Set([
    '/customer-invoices',
    '/payment-receipts',
    '/cheque-register',
    '/purchasing',
    '/draft-purchases',
    '/draft-payments',
    '/supplier-payments',
    '/accounting',
    '/chart-of-accounts',
    '/journal-entries',
    '/vendors',
    '/billing',
    '/payroll/runs',
    '/network',
    '/users',
    '/activity-logs',
    '/subscription/status',
    '/reports',
    '/reports/sales-report',
    '/inventory/products',
    '/inventory/suppliers',
    '/inventory/stock-movements',
    '/inventory/stock-adjustment',
    '/field/draft-order',
    '/field/draft-recovery',
]);

/** Quick links shown on role workspace dashboards. */
export const WORKSPACE_QUICK_LINKS = {
    field: [
        { label: 'New Draft Invoice', path: '/customer-invoices?create=1', permission: 'invoices:create', primary: true },
        { label: 'New Draft Receipt', path: '/payment-receipts?create=1', permission: 'paymentsReceipts:create', primary: true },
        { label: 'Drafts', path: '/drafts', permission: 'drafts:view' },
        { label: 'Exception Policies', path: '/exceptions?tab=policies', permission: 'drafts:view' },
        { label: 'Customers', path: '/customers', permission: 'customers:view' },
        { label: 'Product Catalog', path: '/field/catalog' },
        { label: 'Daily Check-in', path: '/field/my-day' },
        { label: "Today's Route", path: '/field/my-route' },
    ],
    operations: [
        { label: 'Invoices', path: '/customer-invoices', permission: 'invoices:view', primary: true },
        { label: 'Receipts', path: '/payment-receipts', permission: 'paymentsReceipts:view', primary: true },
        { label: 'Purchase Bills', path: '/purchasing', permission: 'products:view' },
        { label: 'Daily Closing', path: '/operations/daily-closing' },
        { label: 'Reconciliation', path: '/operations/reconciliation' },
        { label: 'Exceptions', path: '/operations/exceptions', permission: 'drafts:view' },
        { label: 'Customers', path: '/customers', permission: 'customers:view' },
    ],
    warehouse: [
        { label: 'Stock Position', path: '/warehouse/stock-position', primary: true },
        { label: 'Goods Receiving', path: '/warehouse/receiving' },
        { label: 'Dispatch', path: '/warehouse/dispatch' },
        { label: 'Stock Transfer', path: '/warehouse/transfer' },
        { label: 'Adjustments', path: '/warehouse/adjustment' },
        { label: 'Products', path: '/inventory/products', permission: 'products:view' },
    ],
};

/**
 * Filter ERP sidebar items for the current role workspace.
 */
export function filterNavItemForRole(userRole, item) {
    if (!item?.path) return true;

    if (userRole === 'Salesman' && SALESMAN_HIDDEN_PATHS.has(item.path)) {
        return false;
    }

    if (hasRoleWorkspace(userRole) && item.path === '/dashboard') {
        return false;
    }

    if (item.path === '/activity-logs' && !canAccessActivityLogs(userRole)) {
        return false;
    }

    return true;
}

/**
 * Lean purpose-driven workspace menus.
 * iconKey is resolved in DashboardLayout (keeps this file free of React nodes).
 */
export const WORKSPACE_SIDEBAR_ITEMS = {
    Salesman: [
        { text: "Today's Route", path: '/field/my-route', iconKey: 'route' },
        { text: 'My Day', path: '/field/my-day', iconKey: 'myDay' },
        { text: 'Tasks', path: '/tasks', module: 'tasks', action: 'view', iconKey: 'tasks', badge: 'pendingTasks' },
        { text: 'Product Catalog', path: '/field/catalog', iconKey: 'catalog' },
        { text: 'Drafts', path: '/drafts', module: 'drafts', action: 'view', iconKey: 'invoice' },
        { text: 'My Exceptions', path: '/exceptions', iconKey: 'exceptions' },
        { text: 'My Incentives', path: '/incentives/my', module: 'incentives', action: 'view', iconKey: 'incentives' },
        { text: 'Customers', path: '/customers', module: 'customers', action: 'view', iconKey: 'customers' },
        { text: 'Employee Portal', path: '/self-service', iconKey: 'portal' },
    ],
    AccountsStaff: [
        { text: 'My Day', path: '/operations/my-day', iconKey: 'myDay' },
        { text: 'Tasks', path: '/tasks', module: 'tasks', action: 'view', iconKey: 'tasks', badge: 'pendingTasks' },
        { text: 'Employee Portal', path: '/self-service', iconKey: 'portal' },
    ],
    Accountant: [
        { text: 'My Day', path: '/operations/my-day', iconKey: 'myDay' },
        { text: 'Tasks', path: '/tasks', module: 'tasks', action: 'view', iconKey: 'tasks', badge: 'pendingTasks' },
        { text: 'Employee Portal', path: '/self-service', iconKey: 'portal' },
    ],
    WarehouseStaff: [
        { text: 'My Day', path: '/warehouse/my-day', iconKey: 'myDay' },
        { text: 'Tasks', path: '/tasks', module: 'tasks', action: 'view', iconKey: 'tasks', badge: 'pendingTasks' },
        { text: 'Stock Position', path: '/warehouse/stock-position', iconKey: 'inventory' },
        { text: 'Goods Receiving', path: '/warehouse/receiving', iconKey: 'receiving' },
        { text: 'Dispatch', path: '/warehouse/dispatch', iconKey: 'dispatch' },
        { text: 'Stock Transfer', path: '/warehouse/transfer', iconKey: 'adjust' },
        { text: 'Adjustments', path: '/warehouse/adjustment', iconKey: 'adjust' },
        { text: 'Products', path: '/inventory/products', module: 'products', action: 'view', iconKey: 'catalog' },
        { text: 'Employee Portal', path: '/self-service', iconKey: 'portal' },
    ],
    /** Area Sales Manager — field coaching & territory execution */
    ASM: [
        { text: 'My Day', path: '/asm/my-day', iconKey: 'myDay' },
        { text: 'Tasks', path: '/tasks', module: 'tasks', action: 'view', iconKey: 'tasks', badge: 'pendingTasks' },
        { text: 'My Incentives', path: '/incentives/my', module: 'incentives', action: 'view', iconKey: 'incentives' },
        { text: 'Drafts', path: '/drafts', module: 'drafts', action: 'view', iconKey: 'invoice' },
        { text: 'Field Ops', path: '/asm/field', iconKey: 'field' },
        { text: 'Product Catalog', path: '/asm/catalog', module: 'products', action: 'view', iconKey: 'catalog' },
        { text: 'Reports', path: '/reports', iconKey: 'performance' },
        { text: 'Exceptions', path: '/exceptions', iconKey: 'exceptions' },
        { text: 'Customers', path: '/customers', module: 'customers', action: 'view', iconKey: 'customers' },
        { text: 'Employee Portal', path: '/self-service', iconKey: 'portal' },
    ],
    /** Zonal Sales Manager — zone performance & approvals */
    ZSM: [
        { text: 'My Day', path: '/zsm/my-day', iconKey: 'myDay' },
        { text: 'Tasks', path: '/tasks', module: 'tasks', action: 'view', iconKey: 'tasks', badge: 'pendingTasks' },
        { text: 'Network Performance', path: '/zsm/performance', iconKey: 'performance' },
        { text: 'Reports', path: '/reports', iconKey: 'performance' },
        { text: 'Exceptions', path: '/exceptions', iconKey: 'exceptions' },
        { text: 'Employee Portal', path: '/self-service', iconKey: 'portal' },
    ],
    /** Regional Sales Manager — region P&L lens */
    RSM: [
        { text: 'My Day', path: '/rsm/my-day', iconKey: 'myDay' },
        { text: 'Tasks', path: '/tasks', module: 'tasks', action: 'view', iconKey: 'tasks', badge: 'pendingTasks' },
        { text: 'Network Performance', path: '/rsm/performance', iconKey: 'performance' },
        { text: 'Reports', path: '/reports', iconKey: 'performance' },
        { text: 'Exceptions', path: '/exceptions', iconKey: 'exceptions' },
        { text: 'Employee Portal', path: '/self-service', iconKey: 'portal' },
    ],
    /** National Sales Manager — executive national view */
    NSM: [
        { text: 'My Day', path: '/nsm/my-day', iconKey: 'myDay' },
        { text: 'Tasks', path: '/tasks', module: 'tasks', action: 'view', iconKey: 'tasks', badge: 'pendingTasks' },
        { text: 'Network Performance', path: '/nsm/performance', iconKey: 'national' },
        { text: 'Reports', path: '/reports', iconKey: 'performance' },
        { text: 'Exceptions', path: '/exceptions', iconKey: 'exceptions' },
        { text: 'Employee Portal', path: '/self-service', iconKey: 'portal' },
    ],
    /** HR — people operations only */
    HRManager: [
        { text: 'My Day', path: '/hr/my-day', iconKey: 'myDay' },
        { text: 'Tasks', path: '/tasks', module: 'tasks', action: 'view', iconKey: 'tasks', badge: 'pendingTasks' },
        { text: 'Employees', path: '/hr/employees', iconKey: 'employees' },
        { text: 'Attendance', path: '/hr/attendance', iconKey: 'attendance' },
        { text: 'Attendance Rules', path: '/hr/attendance-policy', iconKey: 'policy' },
        { text: 'Attendance Issues', path: '/hr/attendance/exceptions', iconKey: 'exceptions' },
        { text: 'Leave', path: '/hr/leave', iconKey: 'leave' },
        { text: 'Payroll', path: '/hr/payroll', iconKey: 'payroll' },
        { text: 'Performance', path: '/hr/performance', iconKey: 'performance' },
        { text: 'Incentives', path: '/incentives/hr', module: 'incentives', action: 'view', iconKey: 'incentives' },
        { text: 'Employee Portal', path: '/self-service', iconKey: 'portal' },
    ],
};

export function getWorkspaceSidebarItems(userRole) {
    return WORKSPACE_SIDEBAR_ITEMS[userRole] || [];
}

export function getWorkspaceNavPrefix(userRole) {
    switch (userRole) {
        case 'Salesman':
            return { label: 'Field Sales', path: '/field/dashboard' };
        case 'AccountsStaff':
        case 'Accountant':
            return { label: 'KPO Workspace', path: '/operations/dashboard' };
        case 'WarehouseStaff':
            return { label: 'Warehouse', path: '/warehouse/dashboard' };
        case 'ZSM':
            return { label: 'ZSM Workspace', path: '/zsm/dashboard' };
        case 'ASM':
            return { label: 'ASM Workspace', path: '/asm/dashboard' };
        case 'RSM':
            return { label: 'RSM Workspace', path: '/rsm/dashboard' };
        case 'NSM':
            return { label: 'NSM Workspace', path: '/nsm/dashboard' };
        case 'HRManager':
            return { label: 'HR Workspace', path: '/hr/dashboard' };
        default:
            return null;
    }
}
